import {
  SourcingCampaignIndex,
  SourcingChannelStatus,
} from 'api-clients/monolith';

import { isNonIntegratedChannel } from '@/utils/channel-util';

/** Short labels that fit a compact Status column (dot icon only). */
const SHORT_STATUS_LABELS = new Set<SourcingChannelStatus>([
  'active',
  'stopped',
  'failed',
]);

/**
 * Pinned Status column widths. DataTable locks left-pinned cells to `size`
 * (width = min = max), so the column must be sized for the widest pill on
 * the current page — Active/Paused vs Completed + non-integrated cloud icon.
 *
 * Defaults to medium when there are no rows (loading) so the column doesn't
 * expand from compact → medium/wide when data arrives. Content can then
 * narrow to compact or widen to wide.
 */
export const STATUS_COLUMN_SIZE = {
  compact: 86,
  medium: 112,
  wide: 120,
} as const;

export function getStatusColumnSize(
  campaigns: Pick<SourcingCampaignIndex, 'status' | 'sourcing_channel'>[],
  { hasReachBreakdown = false }: { hasReachBreakdown?: boolean } = {},
): number {
  // Loading / empty: keep medium so the pinned column doesn't jump wider
  // when the first page of data arrives.
  if (campaigns.length === 0) {
    return STATUS_COLUMN_SIZE.medium;
  }

  let size: number = hasReachBreakdown
    ? STATUS_COLUMN_SIZE.medium
    : STATUS_COLUMN_SIZE.compact;

  for (const campaign of campaigns) {
    const channel = campaign.sourcing_channel;
    if (
      isNonIntegratedChannel(
        channel.is_integratable,
        channel.is_publishable,
        !!channel.is_vonq_channel,
      )
    ) {
      return STATUS_COLUMN_SIZE.wide;
    }
    if (!SHORT_STATUS_LABELS.has(campaign.status)) {
      size = STATUS_COLUMN_SIZE.medium;
    }
  }

  return size;
}
